import { useEffect, useRef } from 'react';
import { useWriteContract, useWaitForTransactionReceipt, useBalance } from 'wagmi';
import { contractAddress } from '../../config/contract';
import { abi } from '../../abi';
import { parseEther } from 'viem';
import { useTransactionPopup } from '../TransactionPopup';

interface EthPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress: string;
  stake: boolean;
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
}

export function EthPayment({
  amount,
  userAddress,
  referralAddress,
  stake,
  onError,
  onSuccess,
  onProcessing,
}: EthPaymentProps) {
  // Store transaction data that shouldn't trigger re-renders
  const amountRef = useRef(amount);
  const processedHashes = useRef(new Set<string>());
  
  // Update the ref when amount changes
  useEffect(() => {
    amountRef.current = amount;
  }, [amount]);
  
  const { data: ethBalanceData } = useBalance({ 
    address: userAddress as `0x${string}`
  });
  
  const {
    data: hash,
    isPending,
    writeContract
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ 
    hash,
    confirmations: 2
  });

  const { showTransaction } = useTransactionPopup();

  // Helper function to extract tokens from log
  const extractTokensFromLog = async (log: any): Promise<string | undefined> => {
    try {
      // Skip the 0x prefix
      const dataWithoutPrefix = log.data.startsWith('0x') 
        ? log.data.slice(2) 
        : log.data;
      
      // First 64 chars after prefix are the token amount
      const tokensHexValue = '0x' + dataWithoutPrefix.slice(0, 64);
      
      // Convert to BigInt
      const tokensBigInt = BigInt(tokensHexValue);
      
      console.log('Raw ETH token value from log:', tokensBigInt.toString());
      
      // For ETH network: divide by 10^18 (18 decimals)
      const tokensNumber = Number(tokensBigInt) / 10**18;
      console.log('ETH network token calculation:', tokensBigInt.toString(), '/', '10^18', '=', tokensNumber);
      
      // Format with commas and 2 decimal places
      return tokensNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      console.error("Error extracting tokens from ETH log:", error);
      return undefined;
    }
  };

  const handlePayment = async () => {
    if (!userAddress) {
      onError('Please connect your wallet');
      return;
    }

    // Check if referrer address is valid for EVM
    if (referralAddress && !referralAddress.startsWith('0x')) {
      // Just log the error, don't show to user
      console.error('Invalid referrer address for Ethereum payments');
      return;
    }

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    try {
      const ethValue = parseEther(amount);
      
      if (ethBalanceData && ethValue > ethBalanceData.value) {
        onError(`Insufficient ETH balance. You have ${ethBalanceData.formatted} ETH`);
        return;
      }

      await writeContract({
        address: contractAddress.eth,
        abi,
        functionName: 'buyWithETH',
        args: [stake, referralAddress],
        value: ethValue
      });
      
      // Show pending transaction popup
      if (hash) {
        showTransaction('pending', `Purchasing $QGEN with ${amount} ETH...`, 'eth', hash);
      }

    } catch (err: any) {
      console.error('ETH Transaction failed:', err);
      onError(err.message || 'Transaction failed');
      showTransaction('error', err.message || 'Transaction failed', 'eth');
      // Reset processing state when error occurs
      onProcessing(false);
    }
  };

  // Update processing state
  useEffect(() => {
    onProcessing(isPending || isConfirming);
  }, [isPending, isConfirming, onProcessing]);

  // Handle success completely outside of React's state management
  useEffect(() => {
    // Use a local variable for unsubscribe to avoid state updates
    let unsubscribed = false;
    
    // Check if we have a hash and it hasn't been processed yet
    if (isSuccess && hash && !processedHashes.current.has(hash)) {
      console.log("ETH payment success detected, hash:", hash);
      
      // Mark hash as processed using ref (no state update)
      processedHashes.current.add(hash);
      
      // Call success callback once
      onSuccess(hash);
      
      // Initialize with basic success popup
      showTransaction(
        'success', 
        `Successfully purchased $QGEN with ${amount} ETH${stake ? ' and staked' : ''}`, 
        'eth', 
        hash
      );
      
      // Asynchronously try to extract token information without using state
      setTimeout(() => {
        if (unsubscribed) return;
        
        // Try to get the transaction receipt and extract token info
        (async () => {
          try {
            if (typeof window.ethereum === 'undefined') {
              console.error("Web3 provider not available");
              return;
            }
            
            const receipt = await (window.ethereum as any).request({
              method: 'eth_getTransactionReceipt',
              params: [hash]
            });
            
            if (!receipt || !receipt.logs || !Array.isArray(receipt.logs) || receipt.logs.length === 0) {
              console.error("No usable transaction logs found");
              return;
            }
            
            // Event topic hashes
            const tokensBoughtTopic = "0x9c46ba47a94a38b7e008b5a7a4ae121852c03bd4d17b04e99e99e6a7c734ad2b";
            const tokensStakedTopic = "0xb539ca1e5c8d398ddf1c41c30166f33404941683be4683319b57669a93dad4ef";
            
            // Find relevant log
            let tokenLog = receipt.logs.find((log: any) => 
              log.topics && log.topics[0] && 
              log.topics[0].toLowerCase() === tokensBoughtTopic.toLowerCase()
            );
            
            // Try staked log if not found and staking
            if (!tokenLog && stake) {
              tokenLog = receipt.logs.find((log: any) => 
                log.topics && log.topics[0] && 
                log.topics[0].toLowerCase() === tokensStakedTopic.toLowerCase()
              );
            }
            
            // If still not found, use the last log as fallback
            if (!tokenLog && receipt.logs.length > 0) {
              tokenLog = receipt.logs[receipt.logs.length - 1];
            }
            
            if (!tokenLog || !tokenLog.data) {
              console.error("Could not find log with token data");
              return;
            }
            
            // Extract token amount
            const tokenAmount = await extractTokensFromLog(tokenLog);
            if (!tokenAmount || unsubscribed) return;
            
            // Update popup with token amount
            showTransaction(
              'success', 
              `Successfully purchased $QGEN with ${amount} ETH${stake ? ' and staked' : ''}`, 
              'eth', 
              hash,
              tokenAmount
            );
          } catch (error) {
            console.error("Failed to extract token amount:", error);
          }
        })();
      }, 1000);
    }
    
    return () => {
      unsubscribed = true;
    };
  }, [isSuccess, hash]);

  return { handlePayment };
}